/**
 * @file AI provider and model types.
 *
 * This file contains Zod schemas and types for AI configuration that are safe
 * to import from client components. Server-only functions are in `ai.ts`.
 */

import type { anthropic } from "@ai-sdk/anthropic";
import type { google } from "@ai-sdk/google";
import type { openai } from "@ai-sdk/openai";

import { z } from "zod";

type ExtractModelId<T> = T extends string
  ? string extends T
    ? never
    : T
  : never;

type GetModelId<T> = T extends {
  languageModel: (modelId: infer U) => unknown;
}
  ? ExtractModelId<U>
  : never;

/**
 * Anthropic model identifiers.
 *
 * @see https://docs.anthropic.com/en/docs/about-claude/models
 */
export const AnthropicModel = z
  .enum([
    `claude-haiku-4-5`,
    `claude-opus-4-5`,
    `claude-sonnet-4-5`,
  ] satisfies GetModelId<typeof anthropic>[])
  .describe(`Anthropic model identifier.`);
export type AnthropicModel = z.infer<typeof AnthropicModel>;

/**
 * Google model identifiers.
 *
 * @see https://ai.google.dev/gemini-api/docs/models
 */
export const GoogleModel = z
  .enum([
    `gemini-2.5-flash`,
    `gemini-2.5-flash-lite`,
    `gemini-2.5-pro`,
    `gemini-3-flash-preview`,
    `gemini-3-pro-preview`,
  ] satisfies GetModelId<typeof google>[])
  .describe(`Google model identifier.`);
export type GoogleModel = z.infer<typeof GoogleModel>;

/**
 * OpenAI model identifiers.
 *
 * @see https://platform.openai.com/docs/models
 */
export const OpenAiModel = z
  .enum([
    `gpt-4.1-mini`,
    `gpt-4.1`,
    `gpt-5-mini`,
    `gpt-5-nano`,
    `gpt-5.2-pro`,
    `gpt-5.2`,
  ] satisfies GetModelId<typeof openai>[])
  .describe(`OpenAI model identifier.`);
export type OpenAiModel = z.infer<typeof OpenAiModel>;

/**
 * Thinking/reasoning effort level.
 *
 * Controls how much computational effort the model spends on reasoning.
 * Translates to provider-specific options (e.g., Anthropic's budget_tokens,
 * OpenAI's reasoning_effort, Google's thinking config).
 */
export const AiEffort = z
  .enum([`low`, `medium`, `high`])
  .describe(`Thinking/reasoning effort level. Higher = more reasoning time.`);
export type AiEffort = z.infer<typeof AiEffort>;

/**
 * API key for the AI provider.
 */
export const AiApiKey = z.string().describe(`API key for the AI provider.`);
export type AiApiKey = z.infer<typeof AiApiKey>;

/**
 * AI provider and model configuration.
 *
 * Discriminated union ensures the model is appropriate for the selected provider.
 */
export const AiConfig = z
  .discriminatedUnion(`provider`, [
    z.object({
      model: AnthropicModel.optional(),
      provider: z.literal(`anthropic`).describe(`Anthropic provider.`),
    }),
    z.object({
      model: GoogleModel.optional(),
      provider: z.literal(`google`).describe(`Google provider.`),
    }),
    z.object({
      model: OpenAiModel.optional(),
      provider: z.literal(`openai`).describe(`OpenAI provider.`),
    }),
  ])
  .and(
    z.object({
      effort: AiEffort.optional(),

      seed: z
        .number()
        .int()
        .optional()
        .describe(`Seed for reproducible generation.`),

      temperature: z
        .number()
        .min(0)
        .max(2)
        .optional()
        .describe(
          `Sampling temperature (0-2). Lower = deterministic, higher = creative.`,
        ),
    }),
  )
  .describe(`AI provider and model configuration.`);
export type AiConfig = z.infer<typeof AiConfig>;

/**
 * AI provider Zod schema.
 */
export const AiProvider = z.enum([`anthropic`, `google`, `openai`]);

/**
 * AI provider type.
 */
export type AiProvider = z.infer<typeof AiProvider>;

/**
 * Default models for each provider.
 *
 * Defaults favor a balance between capability and cost/speed.
 */
export const providerDefaultModelMap = {
  anthropic: `claude-haiku-4-5`,
  google: `gemini-2.5-flash-lite`,
  openai: `gpt-5-mini`,
} as const satisfies {
  [Provider in AiProvider]: Extract<AiConfig, { provider: Provider }>[`model`];
};

/**
 * Token usage information from AI generation.
 *
 * @see https://v6.ai-sdk.dev/docs/reference/ai-sdk-core/generate-text#usage
 */
export const AiUsage = z
  .object({
    cachedInputTokens: z
      .number()
      .int()
      .nonnegative()
      .optional()
      .describe(`Number of cached input tokens.`),
    inputTokens: z
      .number()
      .int()
      .nonnegative()
      .optional()
      .describe(`Number of input (prompt) tokens used.`),
    outputTokens: z
      .number()
      .int()
      .nonnegative()
      .optional()
      .describe(`Number of output (completion) tokens used.`),
    reasoningTokens: z
      .number()
      .int()
      .nonnegative()
      .optional()
      .describe(`Number of reasoning tokens used.`),
    totalTokens: z
      .number()
      .int()
      .nonnegative()
      .optional()
      .describe(
        `Total tokens used (may include reasoning tokens or other overhead).`,
      ),
  })
  .describe(`Token usage information from AI generation.`);
export type AiUsage = z.infer<typeof AiUsage>;
